import {HttpErrorResponse} from '@angular/common/http';
import { Component, OnInit, ViewChild, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {EntityHeader} from './entity-header';
import {SelectionItem} from '../selections/selection-item';
import {EntityService} from '../services/entity-service';
import {InputCacheService} from '../services/input-cache-service';
import {SelectionService} from '../services/selection-service';
import {StringUtils} from '../utils/string-utils';
import {EntitySearchCriteria} from './entity-search-criteria';
import {PersonLoginService} from '../services/person-login-service';
import {MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource} from '@angular/material/table';
import {SelectionModel} from '@angular/cdk/collections';
import {MatSort, MatSortHeader} from '@angular/material/sort';
import {MatPaginator} from '@angular/material/paginator';
import {PersonLogin} from '../person/person-login';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {FormsModule} from '@angular/forms';
import { CurrencyPipe, DatePipe } from '@angular/common';

@Component({
    selector: 'entity-search',
    templateUrl: './entity-search.component.html',
    styleUrls: ['./entity-search.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MatButton, MatIcon, MatPaginator, MatProgressSpinner, MatTable, MatSort, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatSortHeader, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, CurrencyPipe, DatePipe]
})
export class EntitySearchComponent implements OnInit {
  private readonly entityService = inject(EntityService);
  private readonly selectionService = inject(SelectionService);
  private readonly inputCacheService = inject(InputCacheService);
  private readonly personLoginService = inject(PersonLoginService);


  @ViewChild(MatSort, {static: true}) sort!: MatSort;
  @ViewChild(MatPaginator, {static: true}) paginator!: MatPaginator;
  readonly entityName = input.required<string>();
  readonly statusFilter = input<string>('');
  readonly newEntityAvailable = input<boolean>(true);
  readonly showAllFilters = input<boolean>(false);
  readonly entitySelected = output<EntityHeader>();
  readonly newEntityRequested = output<string>();
  readonly recentReleasesRequested = output<string>();
  private selection: SelectionModel<EntityHeader> = new SelectionModel<EntityHeader>(false, []);
  private user!: PersonLogin;
  readonly searchResults: MatTableDataSource<EntityHeader> = new MatTableDataSource<EntityHeader>();
  criteria!: EntitySearchCriteria;
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly warningMessage = signal<string>(undefined!);
  readonly organizations = signal<SelectionItem[]>(undefined!);
  readonly regions = signal<SelectionItem[]>(undefined!);
  displayedColumns: string[] = [];

  ngOnInit(): void {
    this.user = this.personLoginService.getCachedLogin()!;
    const showRegionFilter: boolean = this.personLoginService.isInternalOrRsm();
    this.displayedColumns = this.getDisplayColumns();
    this.criteria = this.inputCacheService.criteria || new EntitySearchCriteria();
    this.criteria.entityName = this.entityName();
    if (!this.criteria.orgUnitId) {
      this.criteria.orgUnitId = '';
    }
    if (!this.criteria.region || !showRegionFilter) {
      this.criteria.region = '';
    }
    if (this.statusFilter() && this.statusFilter().length > 0) {
      this.criteria.approvalStatusCode = this.statusFilter();
    } else if (!this.criteria.approvalStatusCode) {
      this.criteria.approvalStatusCode = '';
    }
    if (this.showAllFilters()) {
      this.selectionService.getEmulatedOrganizations().subscribe({
        next: (results: SelectionItem[]) => {
          this.organizations.set(results);
          if (!this.personLoginService.isInternalOrRsm()) {
            this.criteria.orgUnitId = this.user?.person?.primaryOrgUnitId || '';
          } else {
            this.criteria.orgUnitId = '';
            this.criteria.region = '';
          }
        },
      });
      if (showRegionFilter) {
        this.selectionService.getActiveRegionOrgs().subscribe(results => this.regions.set(results));
      }
    } else {
      this.criteria.orgUnitId = '';
      this.criteria.region = '';
    }
    this.search();
  }

  private getDisplayColumns(): string[] {
    if (this.entityName() && this.entityName().length > 0) {
      switch (this.entityName()) {
        case 'CONTRACT':
          return ['entityId', 'projectName', 'createTimestamp', 'poNumber', 'finalExtendedAmount', 'salesRep', 'responsibility',
            'totalUnitQty', 'leadTime', 'smallTextUserField3', 'numericUserField4', 'smallTextUserField1', 'levelOfReportingCode', 'region'];
        case 'QUOTE':
          return ['entityId', 'projectName', 'poNumber', 'salesRep', 'responsibility', 'approvalStatus', 'leadTime', 'finalExtendedAmount',
            'totalUnitQty', 'updateTimestamp', 'region'];
      }
    }
    return [];
  }

  newQuote(): void {
    this.newEntityRequested.emit(this.criteria.orgUnitId);
  }

  rowSelected(header: EntityHeader): void {
    if (!this.selection.isSelected(header)) {
      this.selection.select(header);
      this.entitySelected.emit(header);
    }
  }

  isSelected(header: EntityHeader): boolean {
    return this.selection.isSelected(header);
  }

  navigateToRecentReleases(orgUnitId: string): void {
    if (orgUnitId) {
      this.recentReleasesRequested.emit(orgUnitId);
    } else {
      var orgUnit = this.user.person.primaryOrgUnitId;
      this.recentReleasesRequested.emit(orgUnit);
    }
  }

  search(): void {
    this.inputCacheService.criteria = this.criteria;
    this.clearStatus();
    this.entityService.getList(this.criteria).subscribe(results => {
      results.forEach(result => result.entityId = result.id.entityId + ';' + result.id.entityRevision);
      this.searchResults.data = results;
      this.searchResults.sort = this.sort;
      this.searchResults.paginator = this.paginator;
      this.isBusy.set(false);
      if (results.length === 200) {
        this.warningMessage.set('These results might not include some older entries, please provide more criteria to shorten the list');
      }
    }, error => this.handleError(error));
  }

  private clearStatus(): void {
    this.isBusy.set(true);
    this.errorMessage.set(undefined!);
    this.warningMessage.set(undefined!);
    this.searchResults.data = [];
  }

  private handleError(error: HttpErrorResponse): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.getErrorMessage(error));
  }

}
